import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeftIcon,
    CreditCardIcon,
    UserCircleIcon,
    EnvelopeIcon,
    PhoneIcon,
    CalendarIcon,
    LinkIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    ArrowsRightLeftIcon,
    TicketIcon,
    BellAlertIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getPaymentDetails } from "../api/walletPaymentsApi";

// ============================================================================
// Badges
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const s = status ?? "UNKNOWN";
    const styles: Record<string, string> = {
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        PROCESSING: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        CANCELLED: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
    };
    return (
        <span className={`inline-flex rounded-full px-3 py-1.5 text-sm font-medium ${styles[s] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>
            {s}
        </span>
    );
};

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return "—";
    try {
        return new Intl.DateTimeFormat("en-NG", {
            month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit",
        }).format(new Date(dateStr));
    } catch {
        return dateStr;
    }
};

const formatCurrency = (amount: number | null | undefined, currency = "NGN") => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

// ============================================================================
// Info Row
// ============================================================================

const InfoRow = ({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) => (
    <div className="flex items-start gap-3 py-2">
        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" />
        <div className="min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{value || "—"}</p>
        </div>
    </div>
);

// ============================================================================
// Component
// ============================================================================

export const PaymentDetailsRoute = () => {
    const { paymentId } = useParams<{ paymentId: string }>();
    const navigate = useNavigate();

    const { data, isLoading, error } = useQuery({
        queryKey: ["payment-details", paymentId],
        queryFn: () => getPaymentDetails(paymentId!),
        enabled: !!paymentId,
    });

    const payment = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            </div>
        );
    }
    if (error || !payment) {
        return (
            <div className="py-20 text-center">
                <p className="text-rose-500">{error ? String(error) : "Payment not found"}</p>
                <Button variant="secondary" size="sm" className="mt-4" onClick={() => navigate(-1)}>Go Back</Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back */}
            <button
                onClick={() => navigate("/wallet-payments/payments")}
                className="flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
            >
                <ArrowLeftIcon className="h-4 w-4" /> Back to Payments
            </button>

            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg shadow-violet-500/25">
                        <CreditCardIcon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Payment Details</h1>
                        <div className="mt-1 flex items-center gap-3">
                            <StatusBadge status={payment.status} />
                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                {payment.purpose?.replace(/_/g, " ") ?? "—"}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white">
                    {formatCurrency(payment.amount, payment.currency)}
                </div>
            </div>

            {/* Failure Reason */}
            {payment.failureReason && (
                <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-900 dark:bg-rose-900/20">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-rose-500" />
                    <div>
                        <p className="text-sm font-medium text-rose-800 dark:text-rose-300">Failure Reason</p>
                        <p className="mt-0.5 text-sm text-rose-700 dark:text-rose-400">{payment.failureReason}</p>
                    </div>
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Payment Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Payment Information</h2>
                    <div className="space-y-1">
                        <InfoRow icon={CreditCardIcon} label="Method" value={payment.method?.replace(/_/g, " ")} />
                        <InfoRow icon={CreditCardIcon} label="Channel" value={payment.paymentChannel} />
                        <InfoRow icon={DocumentTextIcon} label="Reference" value={<span className="font-mono text-xs">{payment.reference}</span>} />
                        {payment.externalReference && (
                            <InfoRow icon={LinkIcon} label="External Reference" value={<span className="font-mono text-xs">{payment.externalReference}</span>} />
                        )}
                        <InfoRow icon={CalendarIcon} label="Created" value={formatDate(payment.createdAt)} />
                        <InfoRow icon={CalendarIcon} label="Paid At" value={formatDate(payment.paidAt)} />
                        <InfoRow icon={CalendarIcon} label="Updated" value={formatDate(payment.updatedAt)} />
                    </div>
                </div>

                {/* User Info */}
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">User Information</h2>
                    <div className="space-y-1">
                        <InfoRow icon={UserCircleIcon} label="Name" value={`${payment.user.firstName} ${payment.user.lastName}`} />
                        <InfoRow icon={EnvelopeIcon} label="Email" value={payment.user.email} />
                        <InfoRow icon={PhoneIcon} label="Phone" value={payment.user.phoneNumber || "—"} />
                        <InfoRow icon={UserCircleIcon} label="Role" value={payment.user.role} />
                    </div>
                </div>
            </div>

            {/* Webhook Info */}
            {payment.webhookInfo && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Webhook Information</h2>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Received At</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{formatDate(payment.webhookInfo.webhookReceivedAt)}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Retry Count</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{payment.webhookInfo.webhookRetryCount}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Related Transaction */}
            {payment.relatedTransaction && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Related Transaction</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Amount</p>
                            <p className="mt-0.5 text-sm font-semibold text-slate-900 dark:text-white">
                                {formatCurrency(payment.relatedTransaction.amount, payment.currency)}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Direction</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{payment.relatedTransaction.direction}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Type</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{payment.relatedTransaction.type?.replace(/_/g, " ")}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Status</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{payment.relatedTransaction.status}</p>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/wallet-payments/transactions/${payment.relatedTransaction!.transactionId}`)}
                            >
                                <ArrowsRightLeftIcon className="mr-1.5 h-4 w-4" /> View Transaction
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Related Booking */}
            {payment.relatedBooking && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
                    <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Related Booking</h2>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Booking ID</p>
                            <p className="mt-0.5 font-mono text-xs text-slate-900 dark:text-white">{payment.relatedBooking.bookingId}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Status</p>
                            <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-white">{payment.relatedBooking.status}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Booked At</p>
                            <p className="mt-0.5 text-sm text-slate-900 dark:text-white">{formatDate(payment.relatedBooking.bookedAt)}</p>
                        </div>
                        <div className="flex items-end">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => navigate(`/ride-bookings-trips/bookings/${payment.relatedBooking!.bookingId}`)}
                            >
                                <TicketIcon className="mr-1.5 h-4 w-4" /> View Booking
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
